import { IOrderItem } from '@core/order/models/order-item.model';
import { DateFormatService } from '@core/date/date-format.service';
import { IProductSession } from '@core/product/models/product-session.model';
import { COpenProducts } from '@core/product/models/product-type.type';
import { TranslateService } from '@ngx-translate/core';
import { ProductHelperService } from '@core/product/product-helper.service';
import { DateRangeFormatService } from '@core/date/date-range-format.service';
import { IOrderItemGroup, IOrderItemGroupItem } from '@core/order/models/order-item-group.model';
import { IOrder } from '@core/order/models/order.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { IProductInfo } from '../product/models/product-info.model';
import { IProduct } from '../product/models/product.model';
import { ProductListService } from '@core/product/product-list.service';
import { CMaxOrderItems } from '@core/availability-calculation/availability-calculation.const';

@Injectable({
  providedIn: 'root'
})
export class OrderItemGroupService {
  private orderItemGroups: IOrderItemGroup[] = [];
  private orderItemGroup$ = new BehaviorSubject<IOrderItemGroup[]>(this.orderItemGroups);

  constructor(
    private dateFormatService: DateFormatService,
    private dateRangeFormatService: DateRangeFormatService,
    private translateService: TranslateService,
    private productHelperService: ProductHelperService,
    private productListService: ProductListService
  ) {}

  getOrderItemGroupItems(orderItemGroups: IOrderItemGroup[] = this.orderItemGroups): IOrderItemGroupItem[] {
    return orderItemGroups.reduce((acc: IOrderItemGroupItem[], group: IOrderItemGroup) => {
      return [...acc, ...group.items];
    }, []);
  }

  getOrderItemGroup(): IOrderItemGroup[] {
    return this.orderItemGroups;
  }

  getOrderItemGroup$(): Observable<IOrderItemGroup[]> {
    return this.orderItemGroup$.asObservable();
  }

  setOrderItemGroups(groups: IOrderItemGroup[]) {
    this.orderItemGroups = groups;
    this.orderItemGroup$.next(groups);
  }

  /** Creates a list of order items grouped by time, day, and product */
  buildOrderItemGroups(order: IOrder, isSingleGroup = false): IOrderItemGroup[] {
    const summary: IOrderItemGroup[] = [];
    const basket = order.items;

    basket.forEach((item: IOrderItem) => {
      if (item.quantity === 0) {
        return;
      }

      const summaryItem: IOrderItemGroupItem = structuredClone(item);
      let title = summaryItem.basketGroupTitle || this.createGroupTitle(item, basket);
      let multiLineTitle: string[] | undefined;
      if (Array.isArray(title)) {
        multiLineTitle = title;
        title = title.join(' ');
      }

      const startTime: string | undefined = summaryItem.session?.startTime;
      const ticketQuantity = this.getPerGuestQuantity(item, basket);
      const orderItem = this.getOrderItemForTimeLabel(item);
      const parentProduct = this.productListService.getParentProductById(item.parentProductId);
      const time: string | undefined = this.createTimeLabel(
        orderItem.session,
        orderItem.sessionDiscount?.duration,
        orderItem.hideSessionDuration,
        orderItem.product,
        parentProduct,
        ticketQuantity
      );
      summaryItem.basketGroupTitle = title;
      summaryItem.basketGroupTime = summaryItem.basketGroupTime || time;
      summaryItem.ticketQuantity = item.quantity * item.groupSize + item.additionalGuests;

      summaryItem.optionSetSelections.sort((a, b) => a.displayOrder - b.displayOrder);

      const findGroup = summary.find(
        (summary: IOrderItemGroup) =>
          (summary.title === title && summary.time === summaryItem.basketGroupTime) || isSingleGroup
      );

      if (findGroup) {
        findGroup.items.push(summaryItem);
      } else {
        const date = summaryItem.bookingDate || this.dateFormatService.dateOnlyStringToDate(summaryItem.session?.date);
        const itemGroup: IOrderItemGroup = {
          title,
          date,
          time,
          startTime,
          items: [summaryItem],
          multiLineTitle,
          variation: summaryItem.product.name,
          parentProductId: summaryItem.parentProductId,
          isRescheduled: this.isItemGroupRescheduled(summaryItem),
          session: summaryItem.session,
          uniqueId: summaryItem.uniqueId,
          relatedKey: summaryItem.relatedOrderItemKey
        };
        summary.push(itemGroup);
      }
    });

    // Ensure that every item can be rescheduled in the group and sort by date and time
    return summary
      .map((cSummary: IOrderItemGroup) => {
        cSummary.isRescheduleEnabled = this.isGroupRescheduleEnabled(cSummary);
        return cSummary;
      })
      .sort(
        (sumFirst: IOrderItemGroup, sumSecond: IOrderItemGroup) =>
          this.dateFormatService.addTimeToDate(sumFirst.date, sumFirst.startTime).getTime() -
          this.dateFormatService.addTimeToDate(sumSecond.date, sumSecond.startTime).getTime()
      );
  }

  private getOrderItemForTimeLabel(orderItem: IOrderItem): IOrderItem {
    if (orderItem.productType !== 'package') {
      return orderItem;
    }

    const sessionOrderItems = orderItem.packageOrderItems?.filter((item: IOrderItem) => {
      return item.productType === 'sessionpass';
    });

    // currently we only support one session product in a package so we can return the first sessionOrderItem
    return sessionOrderItems?.length ? sessionOrderItems[0] : orderItem;
  }

  private getDateTitle(itemToTitle: IOrderItemGroupItem, isDateLong = true) {
    const isToday = this.dateFormatService.isSameDay(itemToTitle.session?.date || itemToTitle.bookingDate, new Date());
    return isToday
      ? 'GENERIC.TODAY'
      : isDateLong
        ? this.dateFormatService.dayDateString(itemToTitle.session?.date || itemToTitle.bookingDate)
        : this.dateFormatService.shortDateString(itemToTitle.session?.date || itemToTitle.bookingDate);
  }

  /** Creates the major group title, such as Today, Valid Forever, Valid until {date} etc. */
  createGroupTitle(itemToTitle: IOrderItemGroupItem, basket: IOrderItemGroupItem[]): string | string[] {
    if (!itemToTitle.product) {
      return '';
    }

    const isBookingToday = this.dateFormatService.isSameDay(itemToTitle.bookingDate, new Date());
    // Title for passes (or packages with passes)
    if (itemToTitle.productType === 'pass' || itemToTitle.product.packageSubType?.includes('pass')) {
      if (!!itemToTitle.product.validDays && (!itemToTitle.product.validDaysFromRedemption || isBookingToday)) {
        // title for passes with validDays and NOT validDaysFromRedemption
        // expect 'Valid {number} days from {date}' - standard pass
        return this.translateService.instant('CHECKOUT.VALID_XX_FROM', {
          NUMBER: itemToTitle.product.validDays,
          DATE: this.getDateTitle(itemToTitle, false)
        });
      } else if (itemToTitle.product.validDaysFromRedemption) {
        // title for passes with validDaysFromRedemption and NOT validDays
        // expect 'Valid xx days from first redemption' - standard pass
        return this.translateService.instant('CHECKOUT.VALID_FROM_REDEMPTION', {
          NUMBER: itemToTitle.product.validDaysFromRedemption
        });
      }
    }

    // passes that are valid for xx days are always displayed separately
    if (itemToTitle.addonToProductId) {
      const findAssociatedProduct = basket.find((basketGroupItem: IOrderItemGroupItem) =>
        this.addonOrderItemMatchesParentItem(basketGroupItem, itemToTitle, undefined)
      );

      if (!!findAssociatedProduct && !itemToTitle.session) {
        itemToTitle = findAssociatedProduct;
      }
    }

    if (itemToTitle.productType === 'recurringpass' && itemToTitle.product.recurringSessionDates) {
      const recurringSessionsDates = itemToTitle.product.recurringSessionDates.map((date: string) =>
        this.dateFormatService.dateOnlyStringToDate(date)
      );
      return [
        this.dateRangeFormatService.shortDateRangeString(recurringSessionsDates),
        `(${this.dateRangeFormatService.shortDaysOfWeekRangeString(recurringSessionsDates)})`
      ];
    }

    if (
      itemToTitle.product.isItemSessionBased ||
      itemToTitle.product.isItemDateBased ||
      !!itemToTitle.addonToProductId
    ) {
      return this.getDateTitle(itemToTitle) || '';
    }

    // Addon's by themselves are open products.
    if (itemToTitle.productType === 'addon') {
      return COpenProducts;
    }

    const validRangeProducts = ['giftcard', 'membership', 'wallet'];
    if (!!itemToTitle.product.validDays && validRangeProducts.includes(itemToTitle.productType as string)) {
      if (
        itemToTitle.productType === 'membership' &&
        !this.dateFormatService.isSameDay(itemToTitle.bookingDate, new Date())
      ) {
        const fromDate = this.dateFormatService.formatDateVenueLocale(itemToTitle.bookingDate, 'dd MMM yyyy');
        const toDate = this.dateFormatService.formatDateVenueLocale(
          this.dateFormatService.addDays(itemToTitle.bookingDate, itemToTitle.product.validDays),
          'dd MMM yyyy'
        );
        return this.translateService.instant('CHECKOUT.VALID_FROM_TO', { FROM: fromDate, TO: toDate });
      }

      return this.translateService.instant('CHECKOUT.VALID_XX_FROM_TODAY', {
        NUMBER: itemToTitle.product.validDays
      });
    }

    // otherwise the product is valid from the date of purchase (today)
    return 'CHECKOUT.VALID_FROM_TODAY';
  }

  getPerGuestQuantity(orderItem: IOrderItem, basket: IOrderItem[]) {
    return orderItem.isPerGuestItem
      ? basket
          .filter((item: IOrderItem) => item.isPerGuestItem)
          .map((item: IOrderItem) => item.quantity)
          .reduce((prev: number, next: number) => prev + next, 0)
      : orderItem.quantity;
  }

  getPerGuestProductSessionEndTime(
    session: IProductSession,
    quantity: number,
    product: IProductInfo,
    parentProductId: string | undefined
  ): string | undefined {
    if (!parentProductId) {
      return undefined;
    }

    const parentProduct = this.productListService.getParentProductById(parentProductId);
    if (!parentProduct) {
      return undefined;
    }

    const duration = parentProduct.duration || 0;
    /**
     * Current compromise for Conqueror: we are assuming that all resources in a game-based product have the same capacity, so we can read the capacity from the first resource.
     * This may not be the case for all game-based products moving forward.
     */
    const maxGuestsPerResource = product.locations?.[0]?.capacity || 0;
    const isPerGuest = parentProduct.durationType === 'perGuest' && maxGuestsPerResource;
    if (!isPerGuest) {
      return undefined;
    }

    return this.dateFormatService.addMinutes(
      session.startTime,
      duration * Math.ceil(quantity / Math.ceil(quantity / maxGuestsPerResource))
    );
  }

  private isItemGroupRescheduled(item: IOrderItemGroupItem) {
    return item.isRescheduling && (!!item.session || !item.product.isItemSessionBased);
  }

  private isGroupRescheduleEnabled(group: IOrderItemGroup): boolean {
    return group.items.every(
      (sItem: IOrderItemGroupItem) =>
        sItem.existingBookingItem?.canReschedule &&
        (!!sItem.session || sItem.product.isItemSessionBased || sItem.product.isItemDateBased)
    );
  }

  /** Decorates the time label to return 4:00PM (2 HRS)*/
  private createTimeLabel(
    session: IProductSession | undefined,
    consecutiveSessionLength: string | undefined | null,
    hideSessionDuration: boolean | undefined,
    productItem: IProductInfo,
    parentProduct: IProduct | null = null,
    quantity = 1
  ): string | undefined {
    const isStandAlonePass =
      productItem.type === 'pass' && (!!productItem.validDays || !!productItem.validDaysFromRedemption);
    if (!session || !session.name || isStandAlonePass) {
      return undefined;
    }

    const sessionName = session.name.replace(' ', '');
    if (hideSessionDuration && !productItem.remainingRecurringSessions) {
      return sessionName;
    }

    let length: string | null = null;
    if (!hideSessionDuration) {
      const endTime =
        this.getPerGuestProductSessionEndTime(session, quantity, productItem, parentProduct?.id) || session.endTime;
      length = consecutiveSessionLength
        ? consecutiveSessionLength
        : this.productHelperService.calculateSessionLength(session.startTime, endTime);
    }

    const sessionsLeft = `${productItem.remainingRecurringSessions} ${this.translateService.instant(
      'GENERIC.RECURRING_SESSIONS_REMAINING'
    )}`;
    if (!!length && !!productItem.remainingRecurringSessions) {
      return `${sessionName} (${length} - ${sessionsLeft})`;
    }

    return length ? `${sessionName} (${length})` : `${sessionName} (${sessionsLeft})`;
  }

  /**
   * Determine if an order item is an addon of another order item
   * When only searching for addons, we check for addonAssociatedProduct against the parentProductId
   * @returns boolean if the item matches
   */
  private addonOrderItemMatchesParentItem(
    matchingItem: IOrderItem,
    orderItem: IOrderItem,
    matchingItemSession: IProductSession | undefined
  ): boolean {
    // fail fast if this is the same object we are checking against.
    if (
      (orderItem.productId === matchingItem.productId && orderItem.uniqueId === matchingItem.uniqueId) ||
      matchingItem.addonToProductId ||
      orderItem.relatedOrderItemKey !== matchingItem.relatedOrderItemKey ||
      orderItem.addonToProductId !== matchingItem.parentProductId
    ) {
      return false;
    }

    if (
      matchingItem.product.isItemSessionBased &&
      orderItem.product.isItemSessionBased &&
      !this.productHelperService.isSameSession(orderItem.session, matchingItemSession)
    ) {
      return false;
    }

    if (
      matchingItem.product.isItemDateBased &&
      !this.dateFormatService.isSameDay(orderItem.bookingDate, matchingItem.bookingDate)
    ) {
      return false;
    }

    return true;
  }
}
